import React, { useState } from "react";
import { toast } from "sonner";
import { useGetManagersQuery } from "../../../../redux/services/branchApi";
import { ChevronDown, Check } from "lucide-react";

type FormState = {
    branchName: string;
    location: string;
    address: string;
    branchEmail: string;
    managerId: string;
    contactNumber: string;
};

type Props = {
    onBack: () => void;
    onSubmit: (data: any) => Promise<void>;
};

const BranchUpload = ({ onBack, onSubmit }: Props) => {
    const [form, setForm] = useState<FormState>({
        branchName: "",
        location: "",
        address: "",
        branchEmail: "",
        managerId: "",
        contactNumber: "",
    });

    const [loading, setLoading] = useState(false);
    const [isDropdownOpen, setDropdownOpen] = useState(false);

    const {
        data: managerData,
        isLoading: isManagersLoading,
        isError: isManagersError,
    } = useGetManagersQuery();

    const managers = managerData?.data?.staff_members ?? [];

    const handleChange = (field: keyof FormState, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!form.branchName.trim()) return toast.error("Branch name is required");
        if (!form.managerId.trim()) return toast.error("Please select a manager");

        try {
            setLoading(true);
            await onSubmit(form);
            toast.success("Branch added successfully!");
            onBack();
        } catch (err) {
            console.error(err);
            toast.error("Failed to add branch");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white shadow-md rounded-xl p-6">
            {/* header */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#143887]">Add Branch</h2>
                <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:underline"
                >
                    ← Back
                </button>
            </div>

            <p className="text-sm text-slate-500 mb-6">
                Fill in branch details and assign a manager.
            </p>

            {/* form */}
            <form onSubmit={handleSubmit} className="space-y-6 border-t pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* branch name */}
                    <div>
                        <label className="text-sm font-medium text-[#00032A] mb-1 block">
                            Branch Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.branchName}
                            onChange={(e) => handleChange("branchName", e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            required
                        />
                    </div>

                    {/* manager dropdown */}
                    <div className="relative">
                        <label className="text-sm font-medium text-[#00032A] mb-1 block">
                            Manager
                        </label>

                        {isManagersLoading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                Loading managers...
                            </div>
                        ) : isManagersError ? (
                            <p className="text-sm text-red-500">Failed to load managers</p>
                        ) : (
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setDropdownOpen(!isDropdownOpen)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 flex items-center justify-between focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                                >
                                    <span className="text-gray-700 truncate">
                                        {form.managerId
                                            ? managers.find((m: any) => m.id === form.managerId)?.user
                                                ?.name
                                            : "Select a manager"}
                                    </span>
                                    <ChevronDown
                                        size={18}
                                        className={`transition-transform text-gray-500 ${isDropdownOpen ? "rotate-180" : ""
                                            }`}
                                    />
                                </button>

                                {isDropdownOpen && (
                                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                        <ul className="max-h-48 overflow-y-auto text-sm">
                                            {managers.length > 0 ? (
                                                managers.map((m: any) => (
                                                    <li
                                                        key={m.id}
                                                        onClick={() => {
                                                            handleChange("managerId", m.id);
                                                            setDropdownOpen(false);
                                                        }}
                                                        className={`px-3 py-2 cursor-pointer flex justify-between items-center hover:bg-blue-50 transition ${form.managerId === m.id
                                                                ? "bg-blue-100 text-blue-700 font-medium"
                                                                : "text-gray-700"
                                                            }`}
                                                    >
                                                        {m.user?.name || "Unnamed"}
                                                        {form.managerId === m.id && (
                                                            <Check size={16} className="text-blue-600" />
                                                        )}
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="px-3 py-2 text-gray-400 text-sm">
                                                    No managers found
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* email */}
                    <div>
                        <label className="text-sm font-medium text-[#00032A] mb-1 block">
                            Branch email
                        </label>
                        <input
                            type="email"
                            value={form.branchEmail}
                            onChange={(e) => handleChange("branchEmail", e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            //required
                        />
                    </div>

                    {/* contact */}
                    <div>
                        <label className="text-sm font-medium text-[#00032A] mb-1 block">
                            Contact Number
                        </label>
                        <input
                            type="tel"
                            value={form.contactNumber}
                            onChange={(e) => handleChange("contactNumber", e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            required
                        />
                    </div>

                    {/* location */}
                    <div>
                        <label className="text-sm font-medium text-[#00032A] mb-1 block">
                            Location <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.location}
                            onChange={(e) => handleChange("location", e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            required
                        />
                    </div>

                    {/* address */}
                    <div>
                        <label className="text-sm font-medium text-[#00032A] mb-1 block">
                            Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.address}
                            onChange={(e) => handleChange("address", e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            required
                        />
                    </div>
                </div>

                {/* buttons */}
                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={onBack}
                        className="rounded-lg border px-4 py-2 text-sm hover:bg-slate-100"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="rounded-lg bg-[#143887] text-white px-4 py-2 text-sm hover:bg-blue-700 transition"
                        disabled={loading}
                    >
                        {loading ? "Adding..." : "Add New Branch"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BranchUpload;

